import React from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import axios from 'axios';
import '../../styles/emailCheck.css';

type PageStatus =
  | 'idle'
  | 'loading'
  | 'connected'
  | 'not-connected'
  | 'connecting'
  | 'callback-processing'
  | 'callback-success'
  | 'callback-error'
  | 'error';

type EmailIntegration = {
  id: number;
  provider: string;
  email_address: string;
  status: string;
  default: boolean;
  centralized: boolean;
  connected_from_app: string;
  last_used_at: string;
  created_at: string;
  sharing_type: string;
};

const API_BASE = process.env.REACT_APP_COIPortal_Url;

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    NCToken: localStorage.getItem('NCToken') || '',
    Accept: 'application/json',
  };
}

export default function EmailCheckPage() {
  const [status, setStatus] = React.useState<PageStatus>('idle');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [integration, setIntegration] = React.useState<EmailIntegration | null>(null);

  // ── OAuth callback handler ──────────────────────────────────────────────
  const handleOAuthCallback = React.useCallback(async (code: string, state: string) => {
    setStatus('callback-processing');
    // Clean up URL so params don't persist on refresh
    window.history.replaceState({}, document.title, window.location.pathname);

    try {
      const res = await axios.post(
        `${API_BASE}/api/igigcoi/postLoginCallback`,
        { code, state },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );

      if (res.data?.success === false) {
        setStatus('callback-error');
        setErrorMsg(res.data?.message || 'Email authorization failed.');
      } else {
        setStatus('callback-success');
      }
    } catch (err: any) {
      setStatus('callback-error');
      setErrorMsg(err?.response?.data?.message || 'Failed to complete email authorization.');
    }
  }, []);

  // ── Check email integration status ─────────────────────────────────────
  const checkEmailIntegration = React.useCallback(async () => {
    setStatus('loading');
    setErrorMsg('');
    setIntegration(null);
    try {
      const res = await axios.get(
        `${API_BASE}/api/igigcoi/checkEmailIntegration`,
        { headers: authHeaders() }
      );

      const data = res.data;
      if (data?.has_integration && data?.integration) {
        setIntegration(data.integration);
        setStatus('connected');
      } else {
        setStatus('not-connected');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.message || 'Failed to check email integration status.');
    }
  }, []);

  // ── On mount: check for OAuth callback params first ────────────────────
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get('code');
    const state = params.get('state');

    if (code && state) {
      handleOAuthCallback(code, state);
    } else {
      checkEmailIntegration();
    }
  }, [handleOAuthCallback, checkEmailIntegration]);

  // ── Get Exchange login URL and open in new tab ─────────────────────────
  const handleConnect = async () => {
    setStatus('connecting');
    setErrorMsg('');
    try {
      const res = await axios.get(
        `${API_BASE}/api/igigcoi/emailExchangeLoginUrl`,
        { headers: authHeaders() }
      );

      const data = res.data;
      const redirectUrl: string = data?.url ?? data?.oauth_url ?? '';
      const codeVerifier: string = data?.code_verifier ?? '';

      if (!redirectUrl) {
        setStatus('error');
        setErrorMsg('No authorization URL returned from server.');
        return;
      }

      if (codeVerifier) {
        sessionStorage.setItem('email_exchange_code_verifier', codeVerifier);
      }

      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      setStatus('not-connected');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.message || 'Failed to get Exchange login URL.');
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="emailcheck__pageWrap">
      <div className="emailcheck__card">

        {/* Header */}
        <div className="emailcheck__header">
          <div className="emailcheck__headerIcon">
            <i className="pi pi-envelope" />
          </div>
          <div>
            <h1 className="emailcheck__title">Email Integration</h1>
            <p className="emailcheck__subtitle">Connect your email account to enable sending certificates directly</p>
          </div>
        </div>

        <div className="emailcheck__divider" />

        {/* Body */}
        <div className="emailcheck__body">

          {/* Loading / checking */}
          {(status === 'idle' || status === 'loading') && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__spinnerWrap">
                <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
              </div>
              <p className="emailcheck__stateTitle">Checking connection status...</p>
              <p className="emailcheck__stateDesc">Please wait while we verify your email integration.</p>
            </div>
          )}

          {/* Getting auth URL */}
          {status === 'connecting' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__spinnerWrap">
                <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
              </div>
              <p className="emailcheck__stateTitle">Opening authorization window...</p>
              <p className="emailcheck__stateDesc">Please complete sign-in in the new tab, then re-check status here.</p>
            </div>
          )}

          {/* Processing OAuth callback */}
          {status === 'callback-processing' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__spinnerWrap">
                <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
              </div>
              <p className="emailcheck__stateTitle">Completing authorization...</p>
              <p className="emailcheck__stateDesc">Finalizing your email connection, please wait.</p>
            </div>
          )}

          {/* Callback success */}
          {status === 'callback-success' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__statusIcon emailcheck__statusIcon--success">
                <i className="pi pi-check" />
              </div>
              <p className="emailcheck__stateTitle emailcheck__stateTitle--success">Email Connected Successfully!</p>
              <p className="emailcheck__stateDesc">Your Exchange email account has been authorized and is ready to use.</p>
              <Button
                label="View Integration"
                icon="pi pi-arrow-right"
                iconPos="right"
                className="emailcheck__connectBtn"
                onClick={checkEmailIntegration}
              />
            </div>
          )}

          {/* Callback error */}
          {status === 'callback-error' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__statusIcon emailcheck__statusIcon--error">
                <i className="pi pi-times" />
              </div>
              <p className="emailcheck__stateTitle emailcheck__stateTitle--error">Authorization Failed</p>
              {errorMsg && (
                <p className="emailcheck__stateDesc emailcheck__stateDesc--error">{errorMsg}</p>
              )}
              <div className="emailcheck__actionRow">
                <Button
                  label="Try Again"
                  icon="pi pi-envelope"
                  className="emailcheck__connectBtn"
                  onClick={handleConnect}
                />
                <Button
                  label="Re-check"
                  icon="pi pi-refresh"
                  className="p-button-outlined emailcheck__retryBtn"
                  onClick={checkEmailIntegration}
                />
              </div>
            </div>
          )}

          {/* Connected */}
          {status === 'connected' && integration && (
            <div className="emailcheck__connectedWrap">
              <div className="emailcheck__statusIcon emailcheck__statusIcon--success">
                <i className="pi pi-check" />
              </div>
              <p className="emailcheck__stateTitle emailcheck__stateTitle--success">Email Connected</p>
              <p className="emailcheck__stateDesc">Your email integration is active and ready to use.</p>

              <div className="emailcheck__detailCard">
                <div className="emailcheck__detailRow">
                  <span className="emailcheck__detailLabel"><i className="pi pi-at" /> Email Address</span>
                  <span className="emailcheck__detailValue">{integration.email_address}</span>
                </div>
                <div className="emailcheck__detailRow">
                  <span className="emailcheck__detailLabel"><i className="pi pi-server" /> Provider</span>
                  <span className="emailcheck__detailValue emailcheck__capitalize">{integration.provider}</span>
                </div>
                <div className="emailcheck__detailRow">
                  <span className="emailcheck__detailLabel"><i className="pi pi-info-circle" /> Status</span>
                  <span className={`emailcheck__badge emailcheck__badge--${integration.status}`}>
                    {integration.status}
                  </span>
                </div>
                <div className="emailcheck__detailRow">
                  <span className="emailcheck__detailLabel"><i className="pi pi-desktop" /> Connected From</span>
                  <span className="emailcheck__detailValue">{integration.connected_from_app}</span>
                </div>
                {integration.last_used_at && (
                  <div className="emailcheck__detailRow">
                    <span className="emailcheck__detailLabel"><i className="pi pi-clock" /> Last Used</span>
                    <span className="emailcheck__detailValue">{formatDate(integration.last_used_at)}</span>
                  </div>
                )}
                <div className="emailcheck__detailRow">
                  <span className="emailcheck__detailLabel"><i className="pi pi-calendar" /> Connected On</span>
                  <span className="emailcheck__detailValue">{formatDate(integration.created_at)}</span>
                </div>
              </div>

              <Button
                label="Re-check Status"
                icon="pi pi-refresh"
                className="p-button-outlined emailcheck__retryBtn"
                onClick={checkEmailIntegration}
              />
            </div>
          )}

          {/* Not connected */}
          {status === 'not-connected' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__statusIcon emailcheck__statusIcon--warning">
                <i className="pi pi-exclamation-triangle" />
              </div>
              <p className="emailcheck__stateTitle emailcheck__stateTitle--warning">Email Not Connected</p>
              <p className="emailcheck__stateDesc">
                Your email is not connected yet. Connect your Microsoft Exchange account to start sending certificates directly from your inbox.
              </p>
              <div className="emailcheck__actionRow">
                <Button
                  label="Connect Email"
                  icon="pi pi-envelope"
                  className="emailcheck__connectBtn"
                  onClick={handleConnect}
                />
                <Button
                  label="Re-check"
                  icon="pi pi-refresh"
                  className="p-button-outlined emailcheck__retryBtn"
                  onClick={checkEmailIntegration}
                />
              </div>
            </div>
          )}

          {/* Generic error */}
          {status === 'error' && (
            <div className="emailcheck__stateWrap">
              <div className="emailcheck__statusIcon emailcheck__statusIcon--error">
                <i className="pi pi-times" />
              </div>
              <p className="emailcheck__stateTitle emailcheck__stateTitle--error">Connection Error</p>
              {errorMsg && (
                <p className="emailcheck__stateDesc emailcheck__stateDesc--error">{errorMsg}</p>
              )}
              <Button
                label="Try Again"
                icon="pi pi-refresh"
                className="p-button-outlined emailcheck__retryBtn"
                onClick={checkEmailIntegration}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
